import React, { FC, memo, useState } from 'react';
import { useRequest } from 'ahooks';
import { Button, Input, Space, Spin } from 'udesk-ui';
import { DocumentEditingOutlined } from '@udesk/icons';
import { getCompanysById, putCompanysById } from 'src/api/companys/{id}';
import UdeskLocales from 'UdeskLocales';

interface TemplateProps {
    companyId?: number;
}

const Template: FC<TemplateProps> = memo((props) => {
    const { companyId } = props;

    const placeholder =
        /* 请输入公司业务描述，用于提高大模型质检准确性，建议保持在200字以内 */ UdeskLocales[
            'current'
        ].pages.dashboard.components.companyBackground.index
            .pleaseEnterTheCompanyBusinessDescriptionUsedToImproveTheAccuracyOfLargeModelQualityInspectionItIsRecommendedToKeepWithinWords;

    const [overview, setOverview] = useState<string>();

    const {
        loading: getLoading,
        run: getCompany,
        data: companyData,
    } = useRequest(() => getCompanysById({ segments: { id: companyId! } }), {
        ready: !!companyId,
        refreshDeps: [companyId],
        onSuccess: (res) => {
            setOverview(res.data?.overview);
        },
    });

    const [isEdit, setIsEdit] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const onEdit = () => {
        setIsEdit(true);
    };

    const onOverviewChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setOverview(e.target.value);
    };

    const { run: putCompany, loading: putLoading } = useRequest(
        (overview?: string) =>
            putCompanysById({ id: companyId, overview }, { segments: { id: companyId! } }),
        {
            manual: true,
            ready: !!companyId,
            onSuccess: () => {
                getCompany();
                setIsEdit(false);
            },
        }
    );

    const onSave = () => {
        putCompany(overview);
    };

    const onCancel = () => {
        setIsEdit(false);
        setOverview(companyData?.data?.overview);
    };

    const loading = getLoading || putLoading;
    const displayOverview = companyData?.data?.overview || placeholder;
    // 仅对真实公司背景文案做折叠，避免占用过多页面空间
    const needCollapse = !!companyData?.data?.overview && displayOverview.length > 120;

    return (
        <div>
            <Spin spinning={loading}>
                <div style={{ fontSize: 12 }}>
                    {isEdit ? (
                        <div style={{ position: 'relative' }}>
                            <Input.TextArea
                                rows={3}
                                bordered={false}
                                autoSize={false}
                                style={{
                                    resize: 'none',
                                    backgroundColor: 'rgba(0,0,0,0.05)',
                                    paddingRight: 60,
                                }}
                                placeholder={placeholder}
                                value={overview}
                                onChange={onOverviewChange}
                                // maxLength={200}
                            />

                            <div style={{ position: 'absolute', right: 10, bottom: 10 }}>
                                <Space direction="vertical">
                                    <Button size="small" onClick={onCancel}>
                                        {/* 取消 */}
                                        {
                                            UdeskLocales['current'].pages.dashboard.components
                                                .companyBackground.index.cancel
                                        }
                                    </Button>
                                    <Button type="primary" size="small" onClick={onSave}>
                                        {/* 保存 */}
                                        {
                                            UdeskLocales['current'].pages.dashboard.components
                                                .companyBackground.index.save
                                        }
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    ) : (
                        <Space align="start">
                            <DocumentEditingOutlined
                                style={{ color: '#1a6eff', cursor: 'pointer' }}
                                onClick={onEdit}
                            />
                            <div style={{ maxWidth: '80%' }}>
                                <div
                                    style={{
                                        color: 'rgba(0, 0, 0, 0.45)',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        lineHeight: '20px',
                                        maxHeight: needCollapse && !isExpanded ? 60 : 'none',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {displayOverview}
                                </div>
                                {needCollapse ? (
                                    <Button
                                        type="link"
                                        size="small"
                                        style={{ padding: 0, height: 20 }}
                                        onClick={() => setIsExpanded((prev) => !prev)}
                                    >
                                        {isExpanded
                                            ? UdeskLocales['current'].pages.dashboard.components
                                                  .companyBackground.index.collapse || '收起'
                                            : UdeskLocales['current'].pages.dashboard.components
                                                  .companyBackground.index.expand || '展开'}
                                    </Button>
                                ) : null}
                            </div>
                        </Space>
                    )}
                </div>
            </Spin>
        </div>
    );
});

export default Template;
